// May 2026 — Desktop Quick Tour, spotlight + tooltip rewrite.
//
// The previous version was a single centered modal. The product
// requirement is a guided 4-step spotlight: dim the page, highlight
// each sidebar item in turn with a glowing border + tooltip, then
// finish on the "Connect Instagram" CTA.
//
// Implementation notes:
//   * Pure CSS — no library dependency. Uses 4 fixed-position dim
//     rectangles around the target (top, bottom, left, right) so the
//     target stays clickable and brightly visible.
//   * Recomputes the target rect on resize / scroll so the spotlight
//     follows even if the layout shifts.
//   * Each step targets a ``data-testid``. If the element is missing
//     (e.g. the Connect CTA when IG is already connected), the step
//     auto-skips to the next.
//   * Renders ONLY on desktop (>=768px). Mobile flow untouched —
//     wrapper in OnboardingProvider already skips us on mobile.
import { useEffect, useState, useCallback, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";

const STEPS = [
  {
    selector: '[data-testid="sidebar-automation"]',
    title: "Automate every comment",
    desc: "Create automations that automatically DM anyone who comments on your posts.",
    cta: "Next",
  },
  {
    selector: '[data-testid="sidebar-analytics"]',
    title: "AI-powered analytics",
    desc: "See AI-powered insights about your reels and Instagram growth.",
    cta: "Next",
  },
  {
    selector: '[data-testid="sidebar-link-in-bio"]',
    title: "Link in bio that converts",
    desc: "Create a beautiful bio page that captures leads from every Instagram visitor.",
    cta: "Next",
  },
  {
    selector: '[data-testid="sidebar-ig-connect-cta"]',
    title: "Connect Instagram",
    desc: "Connect your Instagram account to unlock all features.",
    cta: "Get Started →",
    isFinal: true,
  },
];

const PAD = 8;
const TIP_W = 320;
const TIP_GAP = 16;

function useTargetRect(selector) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    const el = document.querySelector(selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [selector]);

  useLayoutEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    // Re-measure once after a short delay to allow layout settle
    // (e.g. fonts loaded, sidebar animations finishing).
    const t = setTimeout(measure, 200);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      clearTimeout(t);
    };
  }, [measure]);

  return rect;
}

export default function DesktopQuickTour({ onClose, onGetStarted }) {
  const [stepIdx, setStepIdx] = useState(0);

  // Auto-skip steps whose target isn't in the DOM (e.g. Connect CTA
  // hidden when IG is already connected). We retry once after 600ms
  // so freshly-mounted sidebars still hit.
  const step = STEPS[stepIdx];
  const rect = useTargetRect(step?.selector);

  useEffect(() => {
    if (!step) return;
    if (rect) return;
    const t = setTimeout(() => {
      const exists = document.querySelector(step.selector);
      if (!exists) {
        if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
        else onClose();
      }
    }, 600);
    return () => clearTimeout(t);
  }, [step, rect, stepIdx, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const next = () => {
    if (step?.isFinal) { onGetStarted?.(); return; }
    if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
    else onClose();
  };

  if (!step) return null;

  // Position tooltip to the right of the target by default; flip
  // to left if it would overflow the viewport.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const tipLeft = rect ? Math.min(
    Math.max(rect.left + rect.width + TIP_GAP, 16),
    vw - TIP_W - 16,
  ) : (vw - TIP_W) / 2;
  const tipTop = rect ? Math.max(16, rect.top - 4) : 80;

  // Spotlight box (slightly inflated) and 4 dim rectangles around it.
  const sb = rect && {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };

  return (
    <div data-testid="desktop-quick-tour" className="fixed inset-0 z-[200] pointer-events-none">
      {/* 4 dim rects around the spotlight (clickable to dismiss).
          When ``sb`` is null (target not measured yet), a single
          full-screen dim covers the page. */}
      {!sb ? (
        <div
          className="absolute inset-0 bg-black/55 pointer-events-auto"
          onClick={onClose}
        />
      ) : (
        <>
          {/* top */}
          <div className="absolute bg-black/55 pointer-events-auto"
               style={{ top: 0, left: 0, right: 0, height: Math.max(0, sb.top) }}
               onClick={onClose} />
          {/* bottom */}
          <div className="absolute bg-black/55 pointer-events-auto"
               style={{ top: sb.top + sb.height, left: 0, right: 0, bottom: 0 }}
               onClick={onClose} />
          {/* left */}
          <div className="absolute bg-black/55 pointer-events-auto"
               style={{ top: sb.top, left: 0, width: Math.max(0, sb.left), height: sb.height }}
               onClick={onClose} />
          {/* right */}
          <div className="absolute bg-black/55 pointer-events-auto"
               style={{ top: sb.top, left: sb.left + sb.width, right: 0, height: sb.height }}
               onClick={onClose} />
          {/* spotlight border */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="absolute rounded-xl"
            style={{
              top: sb.top, left: sb.left, width: sb.width, height: sb.height,
              boxShadow: "0 0 0 2px rgba(20,184,166,0.85), 0 0 32px 6px rgba(20,184,166,0.45)",
            }}
            data-testid="quick-tour-spotlight"
          />
        </>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={stepIdx}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 8 }}
          transition={{ duration: 0.18 }}
          className="absolute pointer-events-auto rounded-xl border border-border bg-background shadow-2xl p-4"
          style={{ top: tipTop, left: tipLeft, width: TIP_W }}
          data-testid={`quick-tour-tooltip-${stepIdx}`}
        >
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-teal-500">
              Step {stepIdx + 1} of {STEPS.length}
            </div>
            <button
              onClick={onClose}
              data-testid="quick-tour-close"
              className="text-muted-foreground hover:text-foreground -m-1 p-1"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="text-base font-semibold mb-1 leading-tight">{step.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">{step.desc}</p>
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              data-testid="quick-tour-skip"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Skip tour
            </button>
            <button
              onClick={next}
              data-testid="quick-tour-next"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold transition"
            >
              {step.cta}
              {!step.isFinal && <ArrowRight className="w-3 h-3" />}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
